import { useEffect, useState, useCallback, useRef } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useParams, useNavigate, useLocation } from "react-router-dom";
import { FileText, Mic, Film, SlidersHorizontal, Wand2, Upload, Home, ChevronRight, ChevronLeft, Loader2, BatteryLow, Crown, Zap, Image as ImageIcon, LifeBuoy, Check, Lock, Coins, ArrowRight, UserCog, X, HardDrive, FolderOpen } from "lucide-react";
import { Trans, useTranslation } from "react-i18next";
import api, { apiErr, fmtCredits, fmtBytes } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useQuota } from "../context/QuotaContext";
import { UploadProvider, TasksBar, useUploads } from "../context/UploadContext";
import { StatusBadge } from "../components/ui-bits";
import ProfileMenu from "../components/ProfileMenu";
import { startProductTour, startChannelTour } from "../lib/productTour";
import { toast } from "sonner";

import TabCanales from "../components/tabs/TabCanales";
import TabVideos from "../components/tabs/TabVideos";
import TabGuion from "../components/tabs/TabGuion";
import TabNarracion from "../components/tabs/TabNarracion";
import TabBroll from "../components/tabs/TabBroll";
import TabComposicion from "../components/tabs/TabComposicion";
import TabRenderizar from "../components/tabs/TabRenderizar";
import TabThumbnail from "../components/tabs/TabThumbnail";
import TabDescarga from "../components/tabs/TabDescarga";
import TabConfig from "../components/tabs/TabConfig";
import TabLibrary from "../components/tabs/TabLibrary";
import OneClickProducer from "../components/OneClickProducer";
import Referidos from "../components/Referidos";
import LogrosRail from "../components/LogrosRail";
import ReferidoBanner from "../components/ReferidoBanner";

const STEPS = [
  { id: "guion", key: "stepGuion", Icon: FileText },
  { id: "narracion", key: "stepNarracion", Icon: Mic },
  { id: "broll", key: "stepBroll", Icon: Film },
  { id: "composicion", key: "stepComposicion", Icon: SlidersHorizontal },
  { id: "renderizar", key: "stepRender", Icon: Wand2 },
  { id: "miniatura", key: "stepThumbnail", Icon: ImageIcon },
  { id: "descarga", key: "stepPublish", Icon: Upload },
];

// Una escena está "lista para renderizar" con el mismo criterio que usa TabRenderizar/el
// backend (start_render): narración completa Y B-roll cubriendo (casi) su duración.
// Solo cuentan las escenas COMPLETAS Y SEGUIDAS desde el principio (igual que el backend).
// Tolerancia 3.5s (no 0.5s): coincide con GRAPHIC_FILL_MIN_GAP del backend — un hueco menor
// que eso lo absorbe el freeze-frame del render sin necesidad de bloquear toda la cola de
// escenas posteriores (bug real: un déficit de ~3s en una escena bloqueaba 10 escenas
// completas después de ella).
function readyScenesCount(project) {
  const segments = project?.segments || [];
  const clips = project?.broll?.clips || [];
  let count = 0;
  for (const s of segments) {
    const au = s.narration_duration || s.duration || 0;
    const complete = s.narration_status === "completo" && au > 0
      && clips.filter((c) => c.segment_id === s.id).reduce((x, c) => x + (c.duration || 0), 0) >= au - 3.5;
    if (complete) count++; else break;
  }
  return count;
}

// Estado de cada paso del wizard derivado del proyecto: completado (✓ verde), bloqueado
// (candado + motivo, con tooltip) o disponible (número normal). Solo se bloquea donde el
// propio backend exige un requisito real (narración pide guion; render pide ≥2 escenas listas)
// — B-roll y Composición no tienen ningún requisito técnico previo, así que nunca se bloquean.
function stepStates(project, t) {
  const segs = project?.segments || [];
  const hasScript = segs.length >= 2;
  const narrationDone = project?.narration?.status === "completo";
  const brollDone = (project?.broll?.coverage || 0) >= 100;
  const ready = readyScenesCount(project);
  const renderDone = project?.render?.status === "completo";
  const hasThumb = !!project?.custom_thumbnail?.url || !!project?.render?.output?.thumbnail;
  return {
    guion: { done: hasScript, lockedReason: null },
    narracion: { done: narrationDone, lockedReason: !hasScript ? t("builder.lockedNoScript") : null },
    broll: { done: brollDone, lockedReason: null },
    composicion: { done: brollDone, lockedReason: null },
    renderizar: { done: renderDone, lockedReason: ready < 2 ? t("builder.lockedNoBroll") : null },
    miniatura: { done: hasThumb, lockedReason: !renderDone ? t("builder.lockedNoRender") : null },
    descarga: { done: false, lockedReason: !renderDone ? t("builder.lockedNoRender") : null },
  };
}

// Qué paso tiene un trabajo de fondo en curso ahora mismo, para pintar un spinner en su
// pestaña — visible incluso si el usuario está mirando OTRA pestaña. El render usa el
// contexto global (isRendering) porque sigue corriendo aunque TabRenderizar esté desmontado;
// guion/narración/YouTube se leen directo de `project` (ver el sondeo en Builder que lo
// mantiene fresco mientras alguno esté activo). broll/composición/miniatura no tienen un
// trabajo de fondo persistente que sondear (son llamadas síncronas), así que nunca están "busy".
function useStepBusy(project, isRendering) {
  return {
    guion: project?.script_gen?.status === "generando",
    narracion: project?.narration?.status === "generando",
    broll: false,
    composicion: false,
    renderizar: !!(project?.id && isRendering(project.id)),
    miniatura: false,
    descarga: project?.youtube_upload?.status === "uploading",
  };
}

// Círculo de estado de un paso: EN CURSO (spinner) tiene prioridad sobre completado/bloqueado
// — es la señal más urgente ("algo se está moviendo ahora mismo").
function StepBadge({ isBusy, active, done, locked, index, size = "md" }) {
  const dim = size === "sm" ? "w-7 h-7" : "w-8 h-8";
  return (
    <span className={`${dim} rounded-full flex items-center justify-center text-sm shrink-0 ${
      isBusy ? "bg-blue-100 text-blue-600"
      : active ? "bg-blue-600 text-white"
      : done ? "bg-emerald-100 text-emerald-700"
      : locked ? "bg-slate-100 text-slate-400"
      : "bg-slate-200 text-slate-600"}`}>
      {isBusy ? <Loader2 size={14} className="animate-spin" />
        : locked ? <Lock size={13} /> : done ? <Check size={15} strokeWidth={3} /> : index + 1}
    </span>
  );
}

// Sidebar en escritorio (vertical, junto al contenido) + barra horizontal en móvil (igual que
// antes) — mismo patrón que el menú lateral del admin. isRendering viene de UploadContext, que
// sigue corriendo aunque TabRenderizar esté desmontado; por eso StepperNav (y no Builder, que
// renderiza el propio UploadProvider) es quien la lee.
function StepperNav({ tab, setTab, steps, project, t }) {
  const { isRendering } = useUploads();
  const busy = useStepBusy(project, isRendering);
  return (
    <>
      <nav className="hidden md:flex md:w-52 md:flex-shrink-0 md:flex-col md:gap-1">
        {STEPS.map((step, i) => {
          const active = tab === step.id;
          const st = steps[step.id] || {};
          const locked = !!st.lockedReason;
          return (
            <button key={step.id} data-testid={`tab-${step.id}`}
              onClick={() => { if (locked) toast.info(st.lockedReason); else setTab(step.id); }}
              title={locked ? st.lockedReason : undefined}
              className={`flex items-center gap-3 rounded-lg px-3 py-2.5 text-left font-semibold text-base transition-colors ${
                active ? "bg-blue-50 text-blue-700"
                : locked ? "text-slate-300 cursor-not-allowed"
                : "text-slate-600 hover:bg-slate-100"}`}>
              <StepBadge isBusy={busy[step.id]} active={active} done={st.done} locked={locked} index={i} />
              {t(`builder.${step.key}`)}
            </button>
          );
        })}
      </nav>
      <nav className="flex md:hidden gap-1 overflow-x-auto overflow-y-hidden vb-scroll pb-1">
        {STEPS.map((step, i) => {
          const active = tab === step.id;
          const st = steps[step.id] || {};
          const locked = !!st.lockedReason;
          return (
            <button key={step.id} data-testid={`tab-mobile-${step.id}`}
              onClick={() => { if (locked) toast.info(st.lockedReason); else setTab(step.id); }}
              title={locked ? st.lockedReason : undefined}
              className={`flex items-center gap-2 px-4 py-3 whitespace-nowrap font-semibold text-base border-b-[3px] transition-colors ${
                active ? "border-blue-600 text-blue-700"
                : locked ? "border-transparent text-slate-300 cursor-not-allowed"
                : "border-transparent text-slate-500 hover:text-slate-800"}`}>
              <StepBadge isBusy={busy[step.id]} active={active} done={st.done} locked={locked} index={i} size="sm" />
              <span className={active ? "inline" : "hidden sm:inline"}>{t(`builder.${step.key}`)}</span>
            </button>
          );
        })}
      </nav>
    </>
  );
}

export default function Builder() {
  const { t } = useTranslation();
  const { user, logout, stopImpersonating } = useAuth();
  const { refreshQuota, quota, openUpgrade, openTopup } = useQuota();
  // La navegación (qué canal/proyecto/pestaña) vive en la URL, no en estado local — antes
  // recargar la página siempre volvía a "Mis Canales" porque view/channel/project/tab eran
  // useState puro sin reflejo en la URL (bug real reportado). channelId/projectId/tabParam
  // son la fuente de verdad; channel/project de abajo son una CACHÉ local que se resuelve a
  // partir de ellos (ver los efectos de sincronización más abajo).
  const { channelId, projectId, tab: tabParam } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const isConfigView = location.pathname === "/app/config";
  const isLibraryView = location.pathname === "/app/library";
  const isReferidosView = location.pathname === "/app/referidos";
  const view = isConfigView ? "config" : isLibraryView ? "library" : isReferidosView ? "referidos"
    : projectId ? "video" : channelId ? "channel" : "home"; // home | channel | video | config | library | referidos
  // Valida contra STEPS por si la URL trae una pestaña inventada/desactualizada — sin esto,
  // un valor desconocido no coincide con ningún tab === "x" del render y deja el contenido en
  // blanco en vez de caer a una pestaña real.
  const tab = STEPS.some((s) => s.id === tabParam) ? tabParam : "guion";
  const [config, setConfig] = useState(null);
  const [channel, setChannel] = useState(null);
  const [project, setProject] = useState(null);
  const queryClient = useQueryClient();

  // Cache de "Mis canales" y "vídeos del canal" (TanStack Query, ya configurado globalmente con
  // staleTime 60s en index.js pero sin usar hasta ahora — cada navegación volvía a pedir todo
  // por red). Al volver a una pantalla ya visitada, se pinta al instante con el dato en caché
  // mientras revalida en segundo plano — nunca se cachean aparte las estadísticas reales de
  // YouTube (vistas/likes/comentarios), esas siguen su propia llamada sin caché en TabVideos.jsx.
  const channelsQuery = useQuery({
    queryKey: ["channels"],
    queryFn: async () => (await api.get("/channels")).data,
  });
  const channels = channelsQuery.data || [];
  const channelsLoaded = channelsQuery.isFetched;
  const loadChannels = useCallback(async () => {
    await queryClient.invalidateQueries({ queryKey: ["channels"] });
  }, [queryClient]);

  // Keyed por channelId (URL), no por channel?.id (caché local) — así los vídeos pueden
  // empezar a cargar en paralelo mientras `channel` todavía se resuelve tras una recarga.
  const videosQuery = useQuery({
    queryKey: ["videos", channelId],
    queryFn: async () => (await api.get(`/projects?channel_id=${channelId}`)).data,
    enabled: !!channelId,
  });
  const videos = videosQuery.data || [];
  const videosLoaded = videosQuery.isFetched;
  const loadVideos = useCallback(async () => {
    if (!channelId) return;
    await queryClient.invalidateQueries({ queryKey: ["videos", channelId] });
  }, [channelId, queryClient]);

  // Resuelve `channel` desde la URL. La versión de la LISTA manda sobre la que ya tuviéramos en
  // memoria, fusionándola encima (`{...cur, ...fromList}`) en vez de sustituirla: la lista acaba
  // de venir del servidor (se refresca al invalidar la query) y además trae MÁS campos que el
  // endpoint de un canal suelto (video_count, youtube, activity...), mientras que el spread
  // conserva cualquier campo que la lista no incluya.
  //
  // BUG real corregido (ago-2026): antes, si ya había un canal en memoria con ese mismo id, se
  // dejaba tal cual y NUNCA se adoptaban los datos frescos. Efecto visible: cambiabas la voz por
  // defecto del canal en su configuración (que sí invalida la query y refresca la lista), volvías
  // al canal y el modal de 1 clic seguía ofreciendo la voz ANTIGUA, porque leía este objeto
  // rancio. Fusionar es seguro para los parches optimistas de updateChannel (recommendations y
  // compañía) porque todos se escriben también en el servidor, así que vuelven en la lista.
  useEffect(() => {
    if (!channelId) { setChannel(null); return; }
    const fromList = channels.find((c) => c.id === channelId) || null;
    setChannel((cur) => {
      if (!fromList) return cur && cur.id === channelId ? cur : null;
      return cur && cur.id === channelId ? { ...cur, ...fromList } : fromList;
    });
  }, [channelId, channels]);

  // Si tras cargar la lista el channelId de la URL no existe (borrado, o de otro usuario),
  // no dejar el spinner girando para siempre — volver a Mis Canales con aviso.
  useEffect(() => {
    if (!channelId || !channelsLoaded) return;
    if (!channels.some((c) => c.id === channelId)) {
      toast.error(t("builder.channelNotFound"));
      navigate("/app", { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [channelId, channelsLoaded, channels]);

  // Resuelve `project` desde la URL con una petición fresca — mismo comportamiento que el
  // openProject() de antes, pero ahora también cubre entrar/recargar directo sobre la URL de
  // un proyecto, no solo la navegación por clic dentro de la app.
  useEffect(() => {
    if (!projectId) { setProject(null); return; }
    let cancelled = false;
    api.get(`/projects/${projectId}`).then(({ data }) => { if (!cancelled) setProject(data); })
      .catch((e) => {
        if (cancelled) return;
        toast.error(apiErr(e));
        navigate(channelId ? `/app/channels/${channelId}` : "/app", { replace: true });
      });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId]);

  const activeCount = (channels || []).filter((c) => c.active !== false).length;
  const overLimit = !!quota && activeCount > (quota.channels_limit || 1);
  const steps = stepStates(project, t);
  const curIdx = STEPS.findIndex((s) => s.id === tab);
  const nextStep = STEPS[curIdx + 1];
  // Mientras el proyecto viene del flujo "1 clic", el contenido lo sustituye
  // OneClickProducer de principio a fin — INCLUIDO el tramo final (miniatura + publicar en
  // YouTube): la promesa es no pisar nunca el asistente de pestañas clásico salvo que el
  // usuario elija explícitamente "Editar manualmente" (dismiss). El stepper también se oculta
  // mientras tanto (pestañas que no responden al clic se perciben como roto).
  // OJO: one_click.status pasa a "rendering" (vía approve o auto_render) y se QUEDA ahí para
  // siempre — nada lo cierra a "completo"/"error", esos estados reales viven aparte en
  // project.render.status/youtube_upload. Por eso la única salida real de esta pantalla es
  // "dismissed" (el propio botón "Editar manualmente"/"Volver al canal" con dismiss) — ni
  // siquiera el render fallido o el vídeo terminado empujan de vuelta a los tabs clásicos,
  // porque OneClickProducer ya sabe presentar el reintento y la pantalla final de publicar.
  const ocStatus = project?.one_click?.status;
  const oneClickActive = !!(ocStatus && ocStatus !== "dismissed");
  // Simétrico al botón "Pasar a vista manual" de dentro de OneClickProducer: si el proyecto
  // ALGUNA VEZ pasó por el 1-clic (tiene `one_click`, aunque esté "dismissed" ahora mismo),
  // se puede volver a esa pantalla desde los tabs clásicos.
  const hasOneClickHistory = !!project?.one_click;

  // Tour guiado (primera visita): solo cuando ya hay al menos 1 canal y el usuario está en la
  // pantalla de canales — evita competir con el wizard de creación, que se auto-abre si no hay
  // ninguno (ver TabCanales). Se marca visto en cuanto termina o se cierra (productTour.js).
  const tourStarted = useRef(false);
  useEffect(() => {
    if (tourStarted.current) return;
    if (!user || !channelsLoaded || view !== "home") return;
    if (channels.length === 0 || user.settings?.tour_seen) return;
    tourStarted.current = true;
    const timer = setTimeout(() => startProductTour(t), 400); // deja pintar el DOM primero
    return () => clearTimeout(timer);
  }, [user, channelsLoaded, channels.length, view, t]);

  // Tour guiado del canal (primera visita a la pantalla de un canal): mismo patrón que el tour
  // de "Mis canales" de arriba, con su propio flag para no competir ni repetirse entre sí.
  const channelTourStarted = useRef(false);
  useEffect(() => {
    if (channelTourStarted.current) return;
    if (!user || view !== "channel" || !channel) return;
    if (user.settings?.channel_tour_seen) return;
    channelTourStarted.current = true;
    const timer = setTimeout(() => startChannelTour(t), 400);
    return () => clearTimeout(timer);
  }, [user, view, channel, t]);

  const refreshProject = useCallback(async () => {
    if (!project?.id) return;
    try { const { data } = await api.get(`/projects/${project.id}`); setProject(data); refreshQuota(); } catch (e) {}
  }, [project?.id, refreshQuota]);

  useEffect(() => {
    api.get("/config").then((r) => setConfig(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const yt = params.get("youtube");
    if (yt === "connected") toast.success(t("builder.ytConnected"));
    else if (yt === "error") toast.error(t("builder.ytError"));
    if (yt) { params.delete("youtube"); window.history.replaceState({}, "", window.location.pathname + (params.toString() ? `?${params}` : "")); }
  }, []);

  useEffect(() => { refreshQuota(); }, [view, tab, refreshQuota]);

  // Sondeo de trabajos de fondo (guion/narración/subida a YouTube) para refrescar `project`
  // aunque el usuario esté en OTRA pestaña — sin esto, el spinner del stepper (ver StepperNav)
  // y el banner inline en TabGuion se quedarían con el último estado leído al cambiar de tab.
  // El render usa su propio mecanismo (UploadContext, ver StepperNav/isRendering) porque ya
  // vive fuera del ciclo de vida de una pestaña concreta. El aviso sonoro/toast al terminar
  // guion lo da la píldora flotante global (UploadContext.startScriptImprove), no este efecto.
  useEffect(() => {
    const active = project?.script_gen?.status === "generando"
      || project?.narration?.status === "generando"
      || project?.youtube_upload?.status === "uploading";
    if (view !== "video" || !active) return;
    const pid = project.id;
    const iv = setInterval(async () => {
      try {
        const { data } = await api.get(`/projects/${pid}`);
        setProject(data);
        const stillActive = data.script_gen?.status === "generando"
          || data.narration?.status === "generando"
          || data.youtube_upload?.status === "uploading";
        if (!stillActive) { clearInterval(iv); refreshQuota(); }
      } catch (e) { /* reintenta */ }
    }, 5000);
    return () => clearInterval(iv);
  }, [view, project?.script_gen?.status, project?.narration?.status, project?.youtube_upload?.status, project?.id, refreshQuota]);

  // Si el usuario supera el límite de canales activos (p. ej. tras bajar de plan
  // con una pestaña abierta dentro de un canal), forzar volver a "Mis Canales"
  // para que elija qué canal mantener antes de poder seguir.
  useEffect(() => {
    if (overLimit && view !== "home" && view !== "config" && view !== "library") {
      navigate("/app");
      toast.info(t("builder.overLimitInfo"));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [overLimit, view]);

  const openChannel = (ch) => {
    if (overLimit || ch.active === false) return;
    navigate(`/app/channels/${ch.id}`);
    // No hace falta pedir los vídeos aquí: videosQuery ya está keyed por channelId y se
    // dispara sola al cambiar — sirve de caché al instante si el usuario ya había entrado
    // antes a este canal, o pide por red la primera vez.
  };

  // initialTab permite entrar directo en una pestaña concreta (p. ej. desde el 1-clic) sin
  // dos navegaciones seguidas — ver onEnterProject/onDismissToEditor más abajo.
  const openProject = (id, initialTab = "guion") => {
    if (!channel) return;
    navigate(`/app/channels/${channel.id}/videos/${id}/${initialTab}`);
  };

  // Desde "Mis Canales" (home) el aviso de actividad ("fallo"/"esperando revisión") ya sabe
  // en qué proyecto concreto — entra directo a él (canal + proyecto) en vez de forzar un paso
  // intermedio por la lista de vídeos del canal, que es lo que hacía antes al reusar openChannel.
  const openChannelProject = (ch, pid) => {
    navigate(`/app/channels/${ch.id}/videos/${pid}/guion`);
  };

  const goHome = () => { navigate("/app"); loadChannels(); };
  const goChannel = () => { if (channel) navigate(`/app/channels/${channel.id}`); loadVideos(); };

  // Deep-link a un canal concreto: ?channel=<id> abre ese canal al entrar (lo usa el botón
  // "Volver" de la página de configuración de canal para regresar al canal en vez de a "Mis
  // Canales"). Solo se aplica una vez, en cuanto los canales están cargados, y se limpia el
  // parámetro de la URL para no reabrirlo si el usuario navega a home dentro de la app.
  const deepLinkDone = useRef(false);
  useEffect(() => {
    if (deepLinkDone.current || !channelsLoaded) return;
    const params = new URLSearchParams(window.location.search);
    const cid = params.get("channel");
    if (!cid) { deepLinkDone.current = true; return; }
    const ch = channels.find((c) => c.id === cid);
    deepLinkDone.current = true;
    params.delete("channel");
    window.history.replaceState({}, "", window.location.pathname + (params.toString() ? `?${params}` : ""));
    if (ch) openChannel(ch);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [channelsLoaded]);

  // Cambiar de pestaña dentro del wizard también es navegación real ahora (URL con el id de
  // pestaña al final) — así refrescar la página dentro de, p. ej., "renderizar" te deja ahí.
  const setTab = (newTab) => {
    if (!channel || !project) return;
    navigate(`/app/channels/${channel.id}/videos/${project.id}/${newTab}`);
  };

  const tabProps = { project, setProject, config, refreshProject, loadVideos, setTab, channel };

  const [resumingOneClick, setResumingOneClick] = useState(false);
  // Aviso "saldo que caduca" (los minutos de PLAN se reinician cada ciclo, a diferencia de la
  // recarga que es permanente) — solo se oculta para esta sesión, vuelve a aparecer si sigue
  // aplicando la próxima vez que se entre (es información real, no un permiso).
  const [dismissExpiring, setDismissExpiring] = useState(false);
  // "Crear otro vídeo" desde la pantalla de éxito del 1-clic: navega al canal y le indica a
  // TabVideos que abra el modal de nuevo vídeo automáticamente en cuanto monte.
  const [autoOpenNewVideo, setAutoOpenNewVideo] = useState(false);
  const resumeOneClick = async () => {
    if (!project?.id) return;
    setResumingOneClick(true);
    try {
      await api.post(`/projects/${project.id}/one-click/resume`);
      await refreshProject();
    } catch (e) { toast.error(apiErr(e)); }
    finally { setResumingOneClick(false); }
  };

  return (
    <UploadProvider>
    <div className="min-h-screen flex flex-col bg-slate-50">
      <header className="bg-white border-b-2 border-slate-200 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-3 sm:px-8 py-2 sm:py-3 flex items-center justify-between gap-2 sm:gap-4">
          <button onClick={goHome} className="flex items-center gap-3 group shrink-0" data-testid="brand-home">
            <img src="/ytmethod_transparent.png" alt="YTMethod" className="h-14 sm:h-16 w-auto group-hover:opacity-80 transition-opacity" />
          </button>

          {/* Botón Volver contextual — solo móvil (en escritorio se usa el breadcrumb) */}
          {view !== "home" && (
            <button
              data-testid="back-button"
              onClick={view === "video" ? goChannel : goHome}
              className="md:hidden flex items-center gap-1 mt-2 text-base font-semibold text-slate-700 hover:text-slate-900 shrink-0 max-w-[110px]"
            >
              <ChevronLeft size={20} className="shrink-0" />
              <span className="truncate">{view === "video" ? (channel?.name || t("common.back")) : t("common.home")}</span>
            </button>
          )}

          <div className="flex items-center gap-2 ml-auto">
            {project && view === "video" && (
              <span className="hidden sm:inline-flex"><StatusBadge status={project.status} /></span>
            )}
            <ProfileMenu user={user} onConfig={() => navigate("/app/config")} onLogout={logout}
              onTour={() => {
                if (view === "channel" && channel) { setTimeout(() => startChannelTour(t), 350); return; }
                navigate("/app"); setTimeout(() => startProductTour(t), 350);
              }} />
          </div>
        </div>

        {/* Barra de tareas en curso (subidas, cortes, render) — entre el logo y el breadcrumb */}
        <TasksBar />

        {/* Breadcrumbs — franja propia a todo el ancho, solo escritorio */}
        {view !== "home" && (
          <div className="hidden md:block border-t border-slate-100 bg-slate-50/60">
            <nav className="max-w-7xl mx-auto px-4 sm:px-8 py-2.5 flex items-center gap-1.5 text-sm sm:text-base text-slate-500 overflow-x-auto overflow-y-hidden vb-scroll" data-testid="breadcrumbs">
              <button onClick={goHome} className={`flex items-center gap-1.5 shrink-0 hover:text-slate-900 transition-colors ${view === "home" ? "text-slate-900 font-semibold" : ""}`}><Home size={17} /> {t("common.home")}</button>
              {channel && (view === "channel" || view === "video") && (
                <>
                  <ChevronRight size={15} className="text-slate-300 shrink-0" />
                  <button onClick={goChannel} className={`truncate hover:text-slate-900 transition-colors ${view === "channel" ? "text-slate-900 font-semibold" : ""}`}>{channel.name}</button>
                </>
              )}
              {project && view === "video" && (
                <>
                  <ChevronRight size={15} className="text-slate-300 shrink-0" />
                  <span className="truncate text-slate-900 font-semibold">{project.name}</span>
                </>
              )}
            </nav>
          </div>
        )}
      </header>

      {user?.impersonated_by && (
        <div data-testid="impersonation-banner" className="bg-amber-500 text-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-8 py-2.5 flex flex-wrap items-center gap-x-3 gap-y-1.5">
            <UserCog size={20} className="shrink-0" />
            <p className="text-sm font-semibold flex-1 min-w-[200px]">
              Modo soporte: estás viendo la cuenta de <b>{user.email}</b> como {user.impersonated_by}.
            </p>
            <button
              data-testid="stop-impersonating-button"
              onClick={stopImpersonating}
              className="inline-flex items-center justify-center gap-2 min-h-[36px] px-4 rounded-lg bg-white text-amber-700 font-bold text-sm hover:bg-amber-50 transition-colors"
            >
              Salir del modo soporte
            </button>
          </div>
        </div>
      )}

      {quota && (quota.seconds_remaining || 0) < 600 && (
        <div data-testid="low-balance-banner" className="bg-rose-50 border-b-2 border-rose-200">
          <div className="max-w-7xl mx-auto px-4 sm:px-8 py-3 flex flex-wrap items-center gap-x-3 gap-y-2">
            <BatteryLow size={20} className="text-rose-600 shrink-0" />
            <p className="text-base text-rose-900 font-medium flex-1 min-w-[200px]">
              <Trans i18nKey="builder.lowBalanceText" values={{ credits: fmtCredits(quota.seconds_remaining || 0) }} components={{ b: <b /> }} />
              {" "}{quota.plan === "gratis" ? t("builder.lowBalanceGratis") : t("builder.lowBalancePaid")}
            </p>
            {quota.plan === "gratis" ? (
              <button data-testid="low-balance-upgrade" onClick={() => openUpgrade()}
                className="inline-flex items-center justify-center gap-2 min-h-[48px] px-4 sm:px-5 rounded-xl bg-rose-600 text-white font-bold text-base hover:bg-rose-700 transition-colors">
                <Crown size={18} /> {t("builder.upgradePlan")}
              </button>
            ) : (
              <button data-testid="low-balance-topup" onClick={() => openTopup()}
                className="inline-flex items-center justify-center gap-2 min-h-[48px] px-4 sm:px-5 rounded-xl bg-rose-600 text-white font-bold text-base hover:bg-rose-700 transition-colors">
                <Zap size={18} /> {t("builder.topup")}
              </button>
            )}
          </div>
        </div>
      )}

      {/* Aviso de almacenamiento cerca del límite del plan — antes solo se veía la barra dentro
          del desplegable de perfil (ProfileMenu), así que había que abrirlo para enterarse.
          Umbral 90%, igual que ese desplegable (storageLow) para que ambos coincidan. Se oculta
          en la propia sección Mi Carpeta: no tiene sentido invitar a ir a un sitio en el que ya
          estás. */}
      {view !== "library" && quota && quota.storage_limit_bytes > 0 && (() => {
        const pct = Math.min(100, Math.round(((quota.storage_used_bytes || 0) / quota.storage_limit_bytes) * 100));
        if (pct < 90) return null;
        return (
          <div data-testid="storage-warning-banner" className="bg-amber-50 border-b-2 border-amber-200">
            <div className="max-w-7xl mx-auto px-4 sm:px-8 py-3 flex flex-wrap items-center gap-x-3 gap-y-2">
              <HardDrive size={20} className="text-amber-600 shrink-0" />
              <p className="text-base text-amber-900 font-medium flex-1 min-w-[200px]">
                <Trans i18nKey="builder.storageWarningText"
                  values={{ pct, used: fmtBytes(quota.storage_used_bytes || 0), limit: fmtBytes(quota.storage_limit_bytes) }}
                  components={{ b: <b /> }} />
              </p>
              <button data-testid="storage-warning-cta" onClick={() => navigate("/app/library")}
                className="inline-flex items-center justify-center gap-2 min-h-[48px] px-4 sm:px-5 rounded-xl bg-amber-500 text-white font-bold text-base hover:bg-amber-600 transition-colors">
                <FolderOpen size={18} /> {t("builder.goToLibrary")}
              </button>
            </div>
          </div>
        );
      })()}

      {/* Aviso de saldo sin gastar. Desde ago-2026 el saldo SE ARRASTRA al mes siguiente, así que
          el mensaje ya no puede decir "se reinician, aprovéchalos": era falso. Ahora hay dos
          mensajes distintos según lo que de verdad esté en juego:
            · en riesgo > 0 → hay saldo POR ENCIMA del tope de arrastre y ese sí se pierde (rojo,
              se muestra en cuanto hay riesgo, sin esperar)
            · en riesgo = 0 → no se pierde nada, solo se sugiere usarlo (azul, informativo) — este
              solo aparece en los últimos 3 días antes de la renovación, si no sería ruido
              constante durante todo el mes */}
      {!dismissExpiring && quota && !quota.payment_overdue && (quota.plan_seconds || 0) > 300 && (() => {
        const enRiesgo = quota.plan_seconds_en_riesgo || 0;
        const pierde = enRiesgo > 0;
        const renewsAt = quota.plan_renews_at ? new Date(quota.plan_renews_at) : null;
        const daysToRenewal = renewsAt ? (renewsAt.getTime() - Date.now()) / 86400000 : null;
        if (!pierde && (daysToRenewal === null || daysToRenewal > 3)) return null;
        return (
          <div data-testid="plan-expiring-banner"
               className={pierde ? "bg-amber-50 border-b-2 border-amber-300" : "bg-blue-50 border-b-2 border-blue-200"}>
            <div className="max-w-7xl mx-auto px-4 sm:px-8 py-3 flex flex-wrap items-center gap-x-3 gap-y-2">
              <Coins size={20} className={`shrink-0 ${pierde ? "text-amber-600" : "text-blue-600"}`} />
              <p className={`text-base font-medium flex-1 min-w-[200px] ${pierde ? "text-amber-900" : "text-blue-900"}`}>
                <Trans i18nKey={pierde ? "builder.planAtRiskText" : "builder.planCarryText"}
                  values={{
                    credits: fmtCredits(quota.plan_seconds || 0), atRisk: fmtCredits(enRiesgo),
                    date: quota.plan_renews_at ? new Date(quota.plan_renews_at).toLocaleDateString(undefined, { dateStyle: "medium" }) : "",
                  }}
                  components={{ b: <b /> }} />
              </p>
              <button data-testid="plan-expiring-dismiss" onClick={() => setDismissExpiring(true)}
                className={`shrink-0 p-2 rounded-lg ${pierde ? "text-amber-500 hover:text-amber-800 hover:bg-amber-100" : "text-blue-400 hover:text-blue-700 hover:bg-blue-100"}`}><X size={18} /></button>
            </div>
          </div>
        );
      })()}

      {/* Progreso hacia el siguiente premio. Va el ULTIMO de la pila de avisos porque es el
          menos urgente: soporte, saldo bajo, almacenamiento y plan por caducar piden una accion;
          esto solo informa. El componente vive aparte (LogrosRail) porque tiene dos vistas y
          logica de ventana propia: aqui dentro eran 40 lineas de JSX en medio de la cabecera. */}
      {/* Los dos avisos comparten sitio y forma, y se turnan segun lo que el usuario este
          haciendo: dentro de un canal esta a punto de hacer un video, asi que manda el contador
          de premios, que es lo unico pegado a esa accion. Mis Canales es el vestibulo, sin tarea
          a medias, y ahi la invitacion no compite con nada. Nunca coinciden, asi que la franja
          es un sitio fijo donde el producto cuenta lo que se puede ganar. En el resto de
          pantallas no hay nada que hacer con ninguno de los dos. */}
      {view === "channel" && <LogrosRail quota={quota} />}
      {view === "home" && <ReferidoBanner />}

      <main className="flex-1 max-w-7xl w-full mx-auto px-4 sm:px-8 py-8">
        {view === "referidos" && (
          <div className="space-y-6 max-w-2xl mx-auto">
            <div>
              <h1 className="text-3xl md:text-4xl font-bold tracking-tight text-slate-900">Invita y gana</h1>
              <p className="text-lg text-slate-600 mt-1">Comparte tu enlace y gana créditos cuando alguien contrate un plan.</p>
            </div>
            <Referidos />
          </div>
        )}
        {view === "home" && <TabCanales channels={channels} channelsLoaded={channelsLoaded} loadChannels={loadChannels} openChannel={openChannel} openChannelProject={openChannelProject} config={config} />}
        {/* channel puede estar momentáneamente null tras una recarga directa sobre la URL del
            canal (se resuelve en cuanto channelsQuery cargue) — spinner en vez de nada. */}
        {view === "channel" && (channel
          ? <TabVideos channel={channel} videos={videos} videosLoaded={videosLoaded} loadVideos={loadVideos} openProject={openProject} config={config} updateChannel={(patch) => setChannel((c) => ({ ...c, ...patch }))} onChannelUpdated={(saved) => { if (saved) setChannel(saved); loadChannels(); }} autoOpenNew={autoOpenNewVideo} onAutoOpenNewHandled={() => setAutoOpenNewVideo(false)} />
          : <div className="py-20 flex justify-center"><Loader2 className="animate-spin text-slate-400" size={40} /></div>)}
        {view === "config" && <TabConfig />}
        {view === "library" && <TabLibrary />}
        {/* Mismo motivo: tras una recarga directa sobre la URL de un proyecto, `project` tarda
            un instante en llegar (petición fresca, ver el efecto de arriba) — sin este
            guardián, oneClickActive/tabProps se evaluarían con project=null y reventaría
            (project.id de OneClickProducer, o los propios Tab* asumiendo un proyecto real). */}
        {view === "video" && !project && (
          <div className="py-20 flex justify-center"><Loader2 className="animate-spin text-slate-400" size={40} /></div>
        )}
        {view === "video" && project && (
          <>
            {/* Mientras el proyecto viene del flujo "1 clic", mostramos la pantalla dedicada
                OneClickProducer en lugar de los tabs normales — de principio a fin, incluida
                la pantalla final de publicar en YouTube. La única salida real es "dismiss"
                (ver oneClickActive arriba). onEnterProject queda como noop de refresco tras
                aprobar el preview (openProject), no como navegación real. */}
            {oneClickActive ? (
              <OneClickProducer
                projectId={project.id}
                onBackToChannel={goChannel}
                onCreateAnother={() => { setAutoOpenNewVideo(true); goChannel(); }}
                onProjectPolled={setProject}
                onEnterProject={(pid) => openProject(pid, "renderizar")}
                onDismissToEditor={async (pid, targetTab) => {
                  // Fase 6-fix: cuando el 1-clic queda en error (o el usuario abandona el
                  // preview), llama al endpoint dismiss para marcar `one_click.status=dismissed`.
                  // Sin esto, el guardián de la línea siguiente vuelve a montar el
                  // OneClickProducer al recargar el proyecto y el usuario se queda "atascado"
                  // en la pantalla de error aunque pulse "Editar manualmente".
                  try { await api.post(`/projects/${pid}/one-click/dismiss`); } catch (e) { /* ignora — el guardián se abre igual al recargar */ }
                  // `targetTab` lo calcula OneClickProducer según en qué paso estaba (p. ej.
                  // "renderizar" si ya estaba renderizando); si no viene (llamadas desde las
                  // pantallas de error, sin ese cálculo), B-roll es el destino por defecto porque
                  // el error habitual ahí es "material no cubrió escenas". Una sola navegación
                  // (antes eran dos llamadas seguidas, openProject + setTab, que competían por
                  // la misma URL).
                  openProject(pid, targetTab || "broll");
                }}
              />
            ) : (
              <>
              {/* Volver a vista 1-clic: en el mismo sitio (arriba a la derecha del contenido,
                  primera fila) que ocupa "Pasar a vista manual" dentro de la cabecera propia de
                  OneClickProducer — mismo eje visual para las dos direcciones del switch. Solo
                  aparece si este proyecto pasó alguna vez por ese flujo (tiene `one_click`) y
                  ahora mismo está descartado ("dismissed"). No relanza nada, solo restaura la
                  pantalla dedicada al estado real del proyecto. */}
              {hasOneClickHistory && (
                <div className="flex justify-end mb-4">
                  <button
                    data-testid="resume-oneclick-button"
                    onClick={resumeOneClick}
                    disabled={resumingOneClick}
                    className="inline-flex items-center justify-center gap-2 min-h-[48px] px-4 sm:px-5 text-base rounded-xl bg-gradient-to-br from-brand-400 to-brand-700 hover:brightness-90 text-white font-bold shadow-sm disabled:opacity-60"
                  >
                    {resumingOneClick ? <Loader2 size={18} className="animate-spin" /> : <Wand2 size={18} />}
                    {t("builder.resumeOneClick")}
                  </button>
                </div>
              )}
              <div className="md:flex md:items-start md:gap-6">
                {/* Stepper: sidebar en escritorio, barra horizontal en móvil (StepperNav
                    decide cuál pintar por breakpoint) — con spinner por paso EN CURSO
                    (guion/narración/render/YouTube) aunque el usuario esté en otra pestaña. */}
                <StepperNav tab={tab} setTab={setTab} steps={steps} project={project} t={t} />
                <div className="min-w-0 flex-1 mt-4 md:mt-0">
                  {tab === "guion" && <TabGuion {...tabProps} />}
                  {tab === "narracion" && <TabNarracion {...tabProps} />}
                  {tab === "broll" && <TabBroll {...tabProps} />}
                  {tab === "composicion" && <TabComposicion {...tabProps} />}
                  {tab === "renderizar" && <TabRenderizar {...tabProps} />}
                  {tab === "miniatura" && <TabThumbnail {...tabProps} />}
                  {tab === "descarga" && <TabDescarga {...tabProps} />}

                  {/* CTA persistente al pie: orienta cuál es el siguiente paso sin tener que
                      adivinarlo en el stepper. No aparece en el último paso. */}
                  {nextStep && (() => {
                    const nextLocked = !!steps[nextStep.id]?.lockedReason;
                    return (
                      <div className="mt-8 pt-6 border-t-2 border-slate-100 flex justify-end">
                        <button data-testid="next-step-cta"
                          onClick={() => { if (nextLocked) toast.info(steps[nextStep.id].lockedReason); else setTab(nextStep.id); }}
                          title={nextLocked ? steps[nextStep.id].lockedReason : undefined}
                          className={`inline-flex items-center justify-center gap-2 min-h-[48px] px-4 sm:px-5 rounded-xl font-bold text-base transition-colors ${
                            nextLocked ? "bg-slate-100 text-slate-400 cursor-not-allowed"
                            : "bg-blue-50 text-blue-700 hover:bg-blue-100"}`}>
                          {nextLocked ? <Lock size={18} /> : null}
                          {t("builder.nextStep", { step: t(`builder.${nextStep.key}`) })}
                          {!nextLocked && <ArrowRight size={18} />}
                        </button>
                      </div>
                    );
                  })()}
                </div>
              </div>
              </>
            )}
          </>
        )}
      </main>

      <footer className="border-t border-slate-200 bg-white" data-testid="private-footer">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 py-5 flex flex-col sm:flex-row items-center justify-between gap-3 text-sm text-slate-500">
          <span>© {new Date().getFullYear()} YTMethod</span>
          <div className="flex flex-wrap items-center justify-center gap-x-6 gap-y-2">
            <Link to="/ayuda" data-testid="footer-help" className="inline-flex items-center gap-1.5 font-semibold text-slate-600 hover:text-blue-700 transition-colors"><LifeBuoy size={16} /> {t("menu.help")}</Link>
            <Link to="/terminos" data-testid="footer-terms" className="hover:text-slate-800 transition-colors">{t("landing.footerTerms")}</Link>
            <Link to="/privacidad" data-testid="footer-privacy" className="hover:text-slate-800 transition-colors">{t("landing.footerPrivacy")}</Link>
          </div>
        </div>
      </footer>
    </div>
    </UploadProvider>
  );
}
